import { createContext, FCWithChildren, useCallback, useState } from 'react';
import { IPermission } from '../models/role';
import { nameof } from '../utils/stringUtils';
import SettingsModal from '../components/shared/SettingsModal';

const DarkModeStorageKey = 'dark-mode';
const FirstDayOfWeekStorageKey = 'first-day-of-week';
const DateFormatStorageKey = 'date-format';
const TimeFormatStorageKey = 'time-format';
const TimeSpanFormatStorageKey = 'timeSpan-format';
const NavGroupOpenStatesStorageKey = 'navGroup-openStates';
const PermissionOverridesStorageKey = 'permission-override';
const NotificationDurationKey = 'notification-duration';

type StorageValue = string | number | boolean | unknown[] | Record<PropertyKey, unknown>;

function getItem(key: string): string | null {
    const item = localStorage.getItem(key);
    return item;
}

function getString(key: string, defaultValue: string): string {
    return getItem(key) ?? defaultValue;
}

function getInt(key: string, defaultValue: number): number {
    const res = getItem(key);
    return res ? Number.parseInt(res) : defaultValue;
}

function getBool(key: string, defaultValue: boolean): boolean {
    const res = getItem(key);
    return res ? getItem(key) === 'true' : defaultValue;
}

function getObj<T extends Record<PropertyKey, unknown>>(key: string, defaultValue: T): T {
    const res = getItem(key);
    return res ? JSON.parse(res) : defaultValue;
}

function setItem(key: string, value: StorageValue) {
    const val = typeof value === 'object' || Array.isArray(value)
        ? JSON.stringify(value)
        : value.toString();

    localStorage.setItem(key, val);
}

export interface Settings {
    isDarkModeEnabled: boolean;
    firstDayOfWeek: number;
    dateFormat: string;
    timeFormat: string;
    timeSpanFormat: string;
    navGroupOpenStates: Record<string, boolean>;
    permissionOverrides: {
        enabled: boolean;
        permissions: IPermission[],
    }
    notificationDurationSec: number;
}

function getDefaultDarkModeValue() {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function useLocalStorageSettings() {
    const [settings, setSettings] = useState<Settings>({
        isDarkModeEnabled: getBool(DarkModeStorageKey, getDefaultDarkModeValue()),
        firstDayOfWeek: getInt(FirstDayOfWeekStorageKey, 1),
        dateFormat: getString(DateFormatStorageKey, 'default'),
        timeFormat: getString(TimeFormatStorageKey, '24hour'),
        timeSpanFormat: getString(TimeSpanFormatStorageKey, 'normal'),
        navGroupOpenStates: getObj<Settings['navGroupOpenStates']>(NavGroupOpenStatesStorageKey, {}),
        permissionOverrides: getObj<Settings['permissionOverrides']>(PermissionOverridesStorageKey, { enabled: false, permissions: [] }),
        notificationDurationSec: getInt(NotificationDurationKey, 5),
    });

    const setIsDarkModeEnabled = useCallback(
        (value: Settings['isDarkModeEnabled']) => {
            setItem(DarkModeStorageKey, value);
            setSettings(curr => ({ ...curr, isDarkModeEnabled: value }));
        },
        [],
    );

    const setFirstDayOfWeekFormat = useCallback(
        (value: Settings['firstDayOfWeek']) => {
            setItem(FirstDayOfWeekStorageKey, value);
            setSettings(curr => ({ ...curr, firstDayOfWeek: value }));
        },
        [],
    );

    const setDateFormat = useCallback(
        (value: Settings['dateFormat']) => {
            setItem(DateFormatStorageKey, value);
            setSettings(curr => ({ ...curr, dateFormat: value }));
        },
        [],
    );

    const setTimeFormat = useCallback(
        (value: Settings['timeFormat']) => {
            setItem(TimeFormatStorageKey, value);
            setSettings(curr => ({ ...curr, timeFormat: value }));
        },
        [],
    );

    const setTimeSpanFormat = useCallback(
        (value: Settings['timeSpanFormat']) => {
            setItem(TimeSpanFormatStorageKey, value);
            setSettings(curr => ({ ...curr, timeSpanFormat: value }));
        },
        [],
    );

    const setNavGroupOpenStates = useCallback(
        (value: Settings['navGroupOpenStates']) => {
            setItem(NavGroupOpenStatesStorageKey, value);
            setSettings(curr => ({ ...curr, navGroupOpenStates: value }));
        },
        [],
    );

    const setPermissionOverride = useCallback(
        (value: Settings['permissionOverrides']) => {
            setItem(PermissionOverridesStorageKey, value);
            setSettings(curr => ({ ...curr, permissionOverrides: value }));
        },
        [],
    );

    const setNotificationDurationSec = useCallback(
        (value: Settings['notificationDurationSec']) => {
            setItem(NotificationDurationKey, value);
            setSettings(curr => ({ ...curr, notificationDurationSec: value }));
        },
        [],
    );

    return {
        settings,
        setIsDarkModeEnabled,
        setFirstDayOfWeekFormat,
        setDateFormat,
        setTimeFormat,
        setTimeSpanFormat,
        setNavGroupOpenStates,
        setPermissionOverride,
        setNotificationDurationSec,
    };
}

export type SettingsHookType = ReturnType<typeof useLocalStorageSettings>;

interface ContextValue {
    settings: Settings;
    openSettingsModal(): void;
    setNavGroupOpenStates: SettingsHookType['setNavGroupOpenStates'];
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const SettingsContext = createContext<ContextValue>(undefined!);

const SettingsProvider: FCWithChildren = ({ children }) => {
    const [settingsOpen, setSettingsOpen] = useState(false);

    const localStorageSettings = useLocalStorageSettings();
    const { settings, setNavGroupOpenStates } = localStorageSettings;

    const openSettingsModal = useCallback(
        () => setSettingsOpen(true),
        [],
    );

    return (
        <SettingsContext.Provider value={{ settings, openSettingsModal, setNavGroupOpenStates }}>
            {children}
            <SettingsModal
                isOpen={settingsOpen}
                onDismiss={() => setSettingsOpen(false)}
                {...localStorageSettings}
            />
        </SettingsContext.Provider>
    );
};

SettingsProvider.displayName = nameof({ SettingsProvider });
export default SettingsProvider;